import { action, makeAutoObservable, runInAction } from 'mobx';

import { isSecondMonitorAvailable } from '@/common/helpers/remote';
import useStore from '@/common/hooks/useStore';

import browserHistory from '../services/history';

enum PresentationAction {
  Destroy = 'destroy-presentation',
  Reconnect = 'reconnect',
  LooseFocus = 'loose-focus',
}

class PresentationStore {
  focused = true;

  started = false;

  presentation: Nullable<PresentationRequest> = null;
  connection: Nullable<PresentationConnection> = null;

  /*
   * Below simple implementation of callback, now it has problem - it can be used only in one Store,
   * in future we probably will have few Stores and this implementation should be updated
   * */
  onTerminate: Nullable<(e: MessageEvent) => void> = null;
  onMessage: Nullable<(e: MessageEvent) => void> = null;

  constructor() {
    makeAutoObservable<PresentationStore, 'subscribe'>(this, {
      presentation: false,
      connection: false,
      onTerminate: false,
      onMessage: false,
      isPresentationWindow: false,
      getConnections: false,
      start: false,
      reconnect: false,
      terminate: false,
      closePresentationWindow: false,
      send: false,
      looseFocus: false,
      subscribe: false,
    });

    // When reload window or close browser - destroy presentation and close presentation window
    window.addEventListener('beforeunload', this.terminate);

    // Automatically subscribe to all messages between windows
    this.subscribe();
  }

  get isPresentationWindow() {
    /*
     * If presentation exists, it means window is opened by host window (host window is a receiver),
     * it means this window is presentation window
     * */
    return Boolean(navigator.presentation?.receiver);
  }

  readonly getConnections = async (): Promise<PresentationConnection[]> => {
    const list = await navigator.presentation?.receiver?.connectionList;

    return list?.connections ?? [];
  };

  start = async (url: string) => {
    try {
      // TODO possible duplicate, need to refactor, check remote.ts
      /*
       * If user has only one monitor connected -  we block presentation,
       * because Presentation API works only for two monitors
       *
       * We make this check here to avoid next case - user see CV presentation mode button,
       * then switch off second monitor and then press this button - in this case we shouldn't start presentation
       * */
      if (!(await isSecondMonitorAvailable())) {
        alert('You need to have at least two monitors to start presentation!');

        return null;
      }
    } catch (e) {
      alert('In order to start presentation, you should grant permission for "Window management" in Chrome Settings!');

      return null;
    }

    const presentation = new PresentationRequest([url]);

    runInAction(() => {
      this.presentation = presentation;
    });

    // Make this presentation the default one when using the "Cast" browser menu.
    navigator.presentation.defaultRequest = presentation;

    const connection = await presentation.start();

    runInAction(() => {
      this.connection = connection;
      this.started = true;
    });

    connection.addEventListener(
      'terminate',
      action((e: MessageEvent) => {
        this.started = false;

        this.onTerminate?.(e);
      }),
    );

    connection.addEventListener('message', this.onReceiveMessage);

    return connection;
  };

  reconnect = (path: string) => {
    this.connection?.send(JSON.stringify({ action: PresentationAction.Reconnect, data: { path } }));
  };

  terminate = () => {
    if (!this.started) {
      return;
    }

    this.connection?.removeEventListener('message', this.onReceiveMessage);
    this.connection?.terminate();
  };

  dispose = () => {
    this.connection = null;
  };

  /*
   * "closePresentationWindow" is the way to close presentation window from inside
   *
   * Idea is to send message to host window and host window will close presentation window
   * */
  closePresentationWindow = async () => {
    const connections = await this.getConnections();

    connections.forEach((connection) => {
      connection.send(
        JSON.stringify({
          action: PresentationAction.Destroy,
        }),
      );
    });
  };

  send = async (message: unknown) => {
    // Send message to stereo presentation
    if (this.connection?.state === 'connected') {
      // When open CV presentation mode, code below will send message FROM parent window TO presentation window
      this.connection?.send(JSON.stringify(message));
    }

    // When open CV presentation mode, code below will send message FROM presentation window TO parent window
    const connections = await this.getConnections();

    connections.forEach((connection) => {
      if (connection.state === 'connected') {
        connection.send(JSON.stringify(message));
      }
    });
  };

  looseFocus = () => {
    if (this.connection && this.connection?.state !== 'terminated' && this.connection?.state !== 'closed') {
      this.connection.send(JSON.stringify({ action: PresentationAction.LooseFocus }));
    }
  };

  private readonly subscribe = async () => {
    this.connection?.addEventListener('message', this.onReceiveMessage);

    const connections = await this.getConnections();

    connections.forEach((connection) => {
      connection.addEventListener('message', this.onReceiveMessage);
    });
  };

  private readonly onReceiveMessage = (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data);

      switch (data.action) {
        case PresentationAction.Destroy:
          this.connection?.terminate();

          return;
        case PresentationAction.Reconnect:
          if (data.data.path) {
            browserHistory.replace(data.data.path);

            this.focused = true;
          }

          return;
        case PresentationAction.LooseFocus:
          this.focused = false;

          return;
        default:
          break;
      }
    } catch (e) {
      return;
    }

    this.onMessage?.(e);
  };
}

export const presentationStore = new PresentationStore();

export const usePresentationStore = () => useStore(PresentationStore);

export default PresentationStore;
